// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Amy → dashboard invalidation bus (ADR-036, architecture-review finding 6).
 *
 * Before this, Amy could rebuild the whole plan, change the calorie target,
 * or log food while the Today/Plan view BEHIND the chat sheet kept showing
 * stale data until a manual reload — the chat said one thing, the screen
 * another. Amy's tool events already stream to the client, so this module
 * turns "a mutating tool finished" into a broadcast the data-owning views
 * subscribe to (see useAmyDataRefresh).
 *
 * regenerate_plan gets extra delayed broadcasts: the tool's own poll gives
 * up at 60s while the job may still be running server-side, so the finished
 * plan must surface without the user asking Amy again.
 */

const listeners = new Set<() => void>()

/** Amy tools whose completion changes state a dashboard renders. */
const MUTATING_TOOLS = new Set([
  "regenerate_plan",
  "update_profile_settings",
  "update_body_metrics",
  "set_calorie_target",
  "log_off_plan_food",
  "record_meal_outcome",
  "apply_plan_change",
  "upsert_pantry_items",
  // Step 0 of the 2026-08-31 mantra roadmap: these three WRITE user state
  // (weight -> Insights chart, tastes -> preference views, confirmed
  // memory -> Memory Center) but never broadcast, so those screens sat
  // stale after Amy changed them.
  "log_weight",
  "update_taste_preferences",
  "confirm_memory",
])

/** Late refetches after a plan rebuild the tool may not have seen finish. */
const REGEN_FOLLOWUP_DELAYS_MS = [20_000, 45_000, 90_000]
let regenTimers: number[] = []

export function subscribeAmyDataChanged(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function notifyAmyDataChanged(): void {
  for (const listener of listeners) listener()
}

export function isMutatingAmyTool(name: string): boolean {
  return MUTATING_TOOLS.has(name)
}

/**
 * Called by the chat when a tool_result arrives for `name`. Broadcasts for
 * every mutating tool; a plan rebuild also schedules the delayed follow-ups
 * (replacing any pending ones, so overlapping rebuilds don't stack timers).
 */
export function noteAmyToolResult(name: string): void {
  if (!isMutatingAmyTool(name)) return
  notifyAmyDataChanged()
  if (name === "regenerate_plan") {
    for (const timer of regenTimers) window.clearTimeout(timer)
    regenTimers = REGEN_FOLLOWUP_DELAYS_MS.map(delay =>
      window.setTimeout(() => notifyAmyDataChanged(), delay)
    )
  }
}
